import { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { namespaces } from "../../../i18n/i18n.constants";
import { useUpdateUserProfileMutation } from "../../../app/api/userApi";
import CustomSpinner from "../../../components/spinner/CustomSpinner";
import { SelectResponse } from "../../../app/models/api/selectResponse";
import CountrySelector from "../../../components/countrySelector/CountrySelector";
import CustomPhoneInput from "../../../components/customPhoneInput/CustomPhoneInput";
import { tinLabelByCountry } from "../../../app/mappers/companyMappers";
import { UserProfileData } from "../../../app/models/api/user/userProfileData";
import { isInvalid, isValid } from "../../../services/form/componentValidation";
import { setErrorMessage, setSuccessMessage } from "../../alerts/commonAlert/commonAlertSlice";
import { useAppDispatch } from "../../../app/store";
import { getBadRequestErrorListFromResponse } from "../../../services/format/response";
import { ErrorResponse } from "../../../app/models/api/errorResponse";
import ChangePasswordModal from "../../../pages/modals/user/changePasswordModal/ChangePasswordModal";

interface Props {
    isLoading: boolean;
    userProfileData?: UserProfileData;
}

const UserProfileForm = ({ isLoading: isProfileLoading, userProfileData }: Props) => {
    const { t } = useTranslation([namespaces.auth, namespaces.validation]);
    const dispatch = useAppDispatch();
    const [updateProfile, { isLoading: isUpdating }] = useUpdateUserProfileMutation();
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    const getInitialValues = () => ({
        firstName: userProfileData?.userPersonalProfile.firstName || "",
        lastName: userProfileData?.userPersonalProfile.lastName || "",
        email: userProfileData?.userPersonalProfile.email || "",
        phoneNumber: userProfileData?.userPersonalProfile.phoneNumber || "",
        companyName: userProfileData?.companyProfile.companyName || "",
        countryCode: userProfileData?.companyProfile.countryCode || "",
        zip: userProfileData?.companyProfile.zip || "",
        city: userProfileData?.companyProfile.city || "",
        tin: userProfileData?.companyProfile.tin || "",
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: yup.object().shape({
            firstName: yup.string().required(t("required.field", { ns: namespaces.validation })),
            lastName: yup.string().required(t("required.field", { ns: namespaces.validation })),
            phoneNumber: yup
                .string()
                .nullable()
                .min(10, t("isPhoneNumber.field", { ns: namespaces.validation }))
                .notRequired(),
            companyName: yup.string().required(t("required.field", { ns: namespaces.validation })),
            countryCode: yup.string().required(t("required.field", { ns: namespaces.validation })),
            zip: yup.string().required(t("required.field", { ns: namespaces.validation })),
            city: yup.string().required(t("required.field", { ns: namespaces.validation })),
            tin: yup.string().notRequired(),
        }),
        onSubmit: async (values) => {
            try {
                const updateRequest: UserProfileData = {
                    userPersonalProfile: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phoneNumber: values.phoneNumber || null,
                    },
                    companyProfile: {
                        companyName: values.companyName,
                        countryCode: values.countryCode,
                        zip: values.zip,
                        city: values.city,
                        tin: values.tin || "",
                    },
                };

                await updateProfile(updateRequest).unwrap();
                dispatch(
                    setSuccessMessage({
                        content: t("userProfile.update.success.message", { ns: namespaces.userAdminPanel }),
                    })
                );
            } catch (error) {
                dispatch(
                    setErrorMessage({
                        content:
                            getBadRequestErrorListFromResponse(error as ErrorResponse) ??
                            t("userProfile.update.error.message", { ns: namespaces.userAdminPanel }),
                    })
                );
            }
        },
    });

    const handleCountrySelect = (item: SelectResponse) => {
        formik.setFieldValue("countryCode", item.value);
    };

    if (isProfileLoading || isUpdating) {
        return <CustomSpinner hideTextTitle={true} />;
    }

    return (
        <div className="user-profile-form">
            <Form onSubmit={formik.handleSubmit}>
                <h2 className="mb-4">{t("userProfile.personalData.title", { ns: namespaces.userAdminPanel })}</h2>
                <Row>
                    <Col md={6}>
                        <Form.Group className="custom-group-controls mb-3" controlId="firstName">
                            <Form.Label className="custom-form-label">{t("signUp.firstNameLbl")}</Form.Label>
                            <Form.Control
                                name="firstName"
                                className="custom-form-control"
                                type="text"
                                aria-label={t("signUp.firstNameLbl")}
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={isValid(formik, "firstName")}
                                isInvalid={isInvalid(formik, "firstName")}
                            />
                            <Form.Control.Feedback type="invalid">{formik.errors.firstName}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="custom-group-controls mb-3" controlId="lastName">
                            <Form.Label className="custom-form-label">{t("signUp.lastNameLbl")}</Form.Label>
                            <Form.Control
                                name="lastName"
                                className="custom-form-control"
                                type="text"
                                aria-label={t("signUp.lastNameLbl")}
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={isValid(formik, "lastName")}
                                isInvalid={isInvalid(formik, "lastName")}
                            />
                            <Form.Control.Feedback type="invalid">{formik.errors.lastName}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group className="custom-group-controls mb-3" controlId="email">
                            <Form.Label className="custom-form-label">{t("login.emailLbl")}</Form.Label>
                            <Form.Control
                                name="email"
                                className="custom-form-control"
                                type="text"
                                aria-label={t("login.emailLbl")}
                                disabled={true}
                                value={formik.values.email}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="custom-group-controls mb-3" controlId="phoneNumber">
                            <Form.Label className="custom-form-label">{t("signUp.phoneNumberLbl")}</Form.Label>
                            <CustomPhoneInput
                                name="phoneNumber"
                                value={formik.values.phoneNumber}
                                onChange={(e) => formik.setFieldValue("phoneNumber", e)}
                                onBlur={formik.handleBlur}
                                isValid={isValid(formik, "phoneNumber")}
                                isInvalid={isInvalid(formik, "phoneNumber")}
                                errors={formik.errors.phoneNumber?.toString()}
                                isDisable={false}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={{ span: 6, offset: 6 }}>
                        <div className="d-flex align-items-end gap-2 mb-3">
                            <Form.Group className="custom-group-controls flex-grow-1 mb-0" controlId="passwordMasked">
                                <Form.Label className="custom-form-label">{t("changePassword.currentPassword")}</Form.Label>
                                <Form.Control
                                    className="custom-form-control"
                                    type="password"
                                    value="********"
                                    disabled={true}
                                    readOnly={true}
                                    aria-label={t("changePassword.currentPassword")}
                                />
                            </Form.Group>
                            <Button
                                variant="outline-primary"
                                className="p-3 text-nowrap"
                                onClick={() => setShowPasswordModal(true)}
                            >
                                {t("changePassword.changePasswordBtn")}
                            </Button>
                        </div>
                    </Col>
                </Row>

                <h2 className="mb-4 mt-5">{t("userProfile.companyData.title", { ns: namespaces.userAdminPanel })}</h2>
                <Form.Group className="custom-group-controls mb-3" controlId="companyName">
                    <Form.Label className="custom-form-label">{t("signUp.companyNameLbl") + "*"}</Form.Label>
                    <Form.Control
                        name="companyName"
                        className="custom-form-control"
                        type="text"
                        aria-label={t("signUp.companyNameLbl")}
                        value={formik.values.companyName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isValid={isValid(formik, "companyName")}
                        isInvalid={isInvalid(formik, "companyName")}
                    />
                    <Form.Control.Feedback type="invalid">{formik.errors.companyName}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="custom-group-controls mb-3" controlId="countryCode">
                    <CountrySelector
                        label={t("signUp.countryLbl") + "*"}
                        defaultValue={formik.values.countryCode}
                        onSelect={handleCountrySelect}
                        isDisable={false}
                    />
                </Form.Group>

                <Row>
                    <Col md={4}>
                        <Form.Group className="custom-group-controls mb-3" controlId="zip">
                            <Form.Label className="custom-form-label">{t("signUp.zipLbl") + "*"}</Form.Label>
                            <Form.Control
                                name="zip"
                                className="custom-form-control"
                                type="text"
                                aria-label={t("login.zipLbl")}
                                value={formik.values.zip}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={isValid(formik, "zip")}
                                isInvalid={isInvalid(formik, "zip")}
                            />
                            <Form.Control.Feedback type="invalid">{formik.errors.zip}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={8}>
                        <Form.Group className="custom-group-controls mb-3" controlId="city">
                            <Form.Label className="custom-form-label">{t("signUp.cityLbl") + "*"}</Form.Label>
                            <Form.Control
                                name="city"
                                className="custom-form-control"
                                type="text"
                                aria-label={t("login.cityLbl")}
                                value={formik.values.city}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={isValid(formik, "city")}
                                isInvalid={isInvalid(formik, "city")}
                            />
                            <Form.Control.Feedback type="invalid">{formik.errors.city}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="custom-group-controls mb-3" controlId="tin">
                    <Form.Label className="custom-form-label">
                        {tinLabelByCountry(formik.values.countryCode)}
                    </Form.Label>
                    <Form.Control
                        name="tin"
                        className="custom-form-control"
                        type="text"
                        aria-label={tinLabelByCountry(formik.values.countryCode)}
                        value={formik.values.tin}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isValid={isValid(formik, "tin")}
                        isInvalid={isInvalid(formik, "tin")}
                    />
                    <Form.Control.Feedback type="invalid">{formik.errors.tin}</Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid d-md-flex justify-content-md-end">
                    <Button
                        className="p-3"
                        variant="primary"
                        type="submit"
                        disabled={isUpdating || formik.isSubmitting || !formik.isValid || !formik.dirty}
                    >
                        {t("buttons.save", { ns: namespaces.common })}
                    </Button>
                </div>
            </Form>
            <ChangePasswordModal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} />
        </div>
    );
};

export default UserProfileForm;
